import React, { useEffect, useState } from "react";
import { DEFAULT_MODEL_ID, modelInfo } from "../../constants";
import { CHECK, CHECK_IDS } from "../../checks";
import { saPost, useSeniorAssociate } from "../../sa";
import { Pill } from "../common/Pill";
import { SAStatusPill } from "../common/SAStatusPill";
import { fetchOpenRouterModels, type OpenRouterModel } from "../../api/client";
import type { Approval, CostState } from "../../types";

export interface UsagePanelProps {
  cost: CostState;
  approvals: Approval[];
}

interface PlaybookEntry {
  description?: string;
  expected?: any;
  expected_range?: number[];
  severity_if_violated?: string;
  rationale?: string;
}

/* The "Usage" tab — metered cost (this run), Senior Associate service status,
   firm playbook (live), recent SA runs, cost-per-decision, per-check breakdown,
   per-email cost, and the rates of the models in use. */
export function UsagePanel({ cost, approvals }: UsagePanelProps): JSX.Element {
  const sa = useSeniorAssociate({ withDetails: true });
  const [orModels, setOrModels] = useState<OpenRouterModel[]>([]);
  useEffect(() => {
    void fetchOpenRouterModels().then((m) => {
      if (m) setOrModels(m);
    });
  }, []);
  const [pbBusy, setPbBusy] = useState<boolean>(false);
  const resetPlaybook = (): void => {
    setPbBusy(true);
    saPost("/playbook/reset")
      .then(() => {
        sa.refresh();
      })
      .finally(() => setPbBusy(false));
  };
  const fmtTime = (s: number): string => {
    try {
      return new Date(s * 1000).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };
  const pbVal = (v: PlaybookEntry): string =>
    v.expected != null
      ? String(v.expected)
      : Array.isArray(v.expected_range)
        ? v.expected_range.join("–") + "%"
        : "—";
  const [showAll, setShowAll] = useState<boolean>(false);
  const ids = Object.keys(cost.perCheck);
  const listIds = showAll ? [...CHECK_IDS, ...ids.filter((id) => !CHECK[id])] : ids;
  const totTok = ids.reduce(
    (s, id) => s + cost.perCheck[id].inTok + cost.perCheck[id].outTok,
    0,
  );
  const apps = Array.isArray(approvals) ? approvals : [];
  const memoFacts = apps.filter((a) => a.type === "deal-memo").length;
  const conflicts = apps.filter((a) => a.type === "conflict").length;
  const decisions = apps.length;
  const dollar = (v: number): string => (v ? "$" + v.toFixed(4) : "—");
  return (
    <div>
      <div className="up">Metered cost · this run</div>
      <div className="bigval">${cost.total.toFixed(4)}</div>
      <div className="tert" style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }}>
        Cost is metered from tokens consumed × each check's model price. It is an output of the
        run, not a setting. {totTok.toLocaleString()} tokens total.
      </div>
      <div className="sastatus" style={{ marginTop: 12, marginBottom: 0 }}>
        <SAStatusPill status={sa.status} info={sa.info} />
        <span style={{ fontSize: 11.5 }}>
          Senior Associate Service — external check service for cross-document consistency, key-number
          reconciliation, and playbook conformance.{" "}
          {sa.status === "offline"
            ? "App falls back to pre-baked findings."
            : sa.status === "online"
              ? "Live findings will augment the pre-baked seed."
              : sa.status === "not_configured"
                ? "Not connected — contact your administrator to enable it."
                : ""}
        </span>
      </div>
      {sa.status === "online" && sa.playbook && (
        <>
          <div
            className="up"
            style={{
              margin: "18px 0 4px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Firm playbook · live</span>
            <button
              className="sel"
              style={{ cursor: pbBusy ? "default" : "pointer", fontSize: 11, padding: "2px 8px" }}
              disabled={pbBusy}
              onClick={resetPlaybook}
            >
              {pbBusy ? "Resetting…" : "Reset to defaults"}
            </button>
          </div>
          <div className="tert" style={{ fontSize: 11.5, marginBottom: 8, lineHeight: 1.5 }}>
            Persisted firm positions the Senior Associate checks conformity against (served from{" "}
            <span className="mono">:8100/playbook</span>). Edits survive restarts.
          </div>
          {Object.entries(sa.playbook as Record<string, PlaybookEntry>).map(([id, v]) => (
            <div className="kv" key={id} title={v.rationale || ""}>
              <span
                className="k"
                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {v.description || id}
              </span>
              <span className="v" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <span className="mono tert" style={{ fontSize: 11 }}>
                  {pbVal(v)}
                </span>
                {v.severity_if_violated && (
                  <Pill
                    color={
                      v.severity_if_violated === "Critical" || v.severity_if_violated === "High"
                        ? "--amber"
                        : "--low"
                    }
                    label={v.severity_if_violated}
                  />
                )}
              </span>
            </div>
          ))}
        </>
      )}
      {sa.status === "online" && sa.runs && (
        <>
          <div
            className="up"
            style={{
              margin: "18px 0 4px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Recent runs · persisted</span>
            <button
              className="sel"
              style={{ cursor: "pointer", fontSize: 11, padding: "2px 8px" }}
              onClick={sa.refresh}
            >
              Refresh
            </button>
          </div>
          <div className="tert" style={{ fontSize: 11.5, marginBottom: 8, lineHeight: 1.5 }}>
            Every live check the service runs is saved to its database (
            <span className="mono">:8100/runs</span>) and survives restarts.
          </div>
          {sa.runs.length === 0 && (
            <div className="empty" style={{ padding: "12px 0" }}>
              No runs persisted yet.
            </div>
          )}
          {sa.runs.map((r: any) => {
            const sm = r.summary || {};
            return (
              <div className="kv" key={r.id} title={"run " + r.id}>
                <span
                  className="k"
                  style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {r.matter || "(no matter)"}{" "}
                  <span className="tert mono" style={{ fontSize: 10.5 }}>
                    {fmtTime(r.created_at)}
                  </span>
                </span>
                <span className="v mono tert" style={{ fontSize: 11 }}>
                  {sm.verdict_pass || 0}P · {sm.verdict_warn || 0}W · {sm.verdict_fail || 0}F
                </span>
              </div>
            );
          })}
        </>
      )}
      {decisions > 0 && (
        <>
          <div className="up" style={{ margin: "18px 0 4px" }}>
            Cost per decision
          </div>
          <div className="tert" style={{ fontSize: 11.5, marginBottom: 8, lineHeight: 1.5 }}>
            What it costs to put one concrete output in front of the lawyer — the number that
            matters when comparing this run to a paralegal hour.
          </div>
          <div className="cpd">
            <div className="cpdcard">
              <div className="cv">{memoFacts ? dollar(cost.total / memoFacts) : "—"}</div>
              <div className="cl">
                per memo fact surfaced
                <br />
                <span className="mono" style={{ color: "var(--tert)" }}>
                  {memoFacts} fact{memoFacts === 1 ? "" : "s"}
                </span>
              </div>
            </div>
            <div className="cpdcard">
              <div className="cv">{conflicts ? dollar(cost.total / conflicts) : "—"}</div>
              <div className="cl">
                per conflict raised
                <br />
                <span className="mono" style={{ color: "var(--tert)" }}>
                  {conflicts} conflict{conflicts === 1 ? "" : "s"}
                </span>
              </div>
            </div>
            <div className="cpdcard">
              <div className="cv">{dollar(cost.total / Math.max(1, decisions))}</div>
              <div className="cl">
                per lawyer decision
                <br />
                <span className="mono" style={{ color: "var(--tert)" }}>
                  {decisions} decision{decisions === 1 ? "" : "s"}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
      <div
        className="up"
        style={{
          margin: "18px 0 8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>By check · model · tokens</span>
        <button
          className="sel"
          style={{ cursor: "pointer", fontSize: 11, padding: "2px 8px" }}
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? "Show ran only" : `Show all checks (${CHECK_IDS.length})`}
        </button>
      </div>
      {listIds.length === 0 && (
        <div className="empty" style={{ padding: "18px 0" }}>
          No usage yet.
        </div>
      )}
      {listIds.map((id) => {
        const u = cost.perCheck[id];
        const meta = CHECK[id] || ({ name: id } as any);
        const name = meta.name || id;
        const isExt = !!meta.external;
        const m = modelInfo(u ? u.model : meta.model, orModels);
        return (
          <div key={id} style={{ marginBottom: 11, opacity: u ? 1 : 0.55 }}>
            <div className="kv" style={{ border: "none", padding: "2px 0" }}>
              <span className="k">
                {name}
                {isExt && <Pill color="--blue" label="API" />}
                {!u && <Pill color="--low" label="didn't run" />}
              </span>
              <span className={u ? "v" : "v tert"}>${(u ? u.cost : 0).toFixed(4)}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 3,
              }}
            >
              <span className="pill" style={{ background: "var(--subtle)", color: "var(--muted)" }}>
                {isExt ? "Senior Associate API · :8100" : m.name}
              </span>
              <span className="mono tert" style={{ fontSize: 11 }}>
                {u && isExt && u.calls
                  ? u.calls +
                    " call" +
                    (u.calls === 1 ? "" : "s") +
                    (u.latency
                      ? " · " +
                        (typeof u.latency === "number"
                          ? Math.round(u.latency) + "ms"
                          : u.latency)
                      : "")
                  : (u ? u.inTok : 0).toLocaleString() +
                    " in · " +
                    (u ? u.outTok : 0).toLocaleString() +
                    " out"}
              </span>
            </div>
          </div>
        );
      })}
      <div className="up" style={{ margin: "18px 0 8px" }}>
        Per email (recent)
      </div>
      {cost.perEmail
        .slice(-8)
        .reverse()
        .map((e, i) => (
          <div className="kv" key={i}>
            <span
              className="k"
              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              #{e.id} {e.subject}
            </span>
            <span className="v">${e.cost.toFixed(4)}</span>
          </div>
        ))}
      <div className="up" style={{ margin: "18px 0 8px" }}>
        Model rates ($/1M tokens)
      </div>
      {(() => {
        const inUse = [...new Set(ids.map((id) => cost.perCheck[id].model).filter(Boolean))];
        return (inUse.length ? inUse : [DEFAULT_MODEL_ID]).map((slug) => {
          const m = modelInfo(slug, orModels);
          return (
            <div className="kv" key={slug}>
              <span className="k">{m.name}</span>
              <span className="v">
                ${m.inP.toFixed(2)} in · ${m.outP.toFixed(2)} out
              </span>
            </div>
          );
        });
      })()}
    </div>
  );
}
